import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Button,
  makeStyles,
  Paper,
  Typography,
  TextField,
  Grid,
  RadioGroup,
  Radio,
  FormControlLabel,
  Divider,
  LinearProgress,
  Box,
} from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { toast } from "react-toastify";
import api from "../../services/api";
import MicIcon from "@material-ui/icons/Mic";
import StopIcon from "@material-ui/icons/Stop";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useBroadcastContext } from "../../context/Broadcast/BroadcastContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 14,
    color: theme.palette.primary.main,
    textTransform: "uppercase",
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  cardBox: {
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "#1e293b" : "#f8fafc",
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  logContainer: {
    maxHeight: 180,
    overflowY: "auto",
    fontFamily: "monospace",
    fontSize: 12,
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === "dark" ? "#0b1120" : "#111827",
    color: "#e2e8f0",
    borderRadius: 8,
    marginTop: theme.spacing(2),
  },
  audioPlayer: {
    width: "100%",
    height: 40,
    marginTop: theme.spacing(1),
  },
}));

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getRandomDelay = (minSec, maxSec) => {
  const min = Math.min(minSec, maxSec);
  const max = Math.max(minSec, maxSec);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const Broadcast = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [mode, setMode] = useState("manual");
  const [manualNumbers, setManualNumbers] = useState("");
  const [csvContacts, setCsvContacts] = useState([]);
  const [csvFileName, setCsvFileName] = useState("");

  const [messageText, setMessageText] = useState("");
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState("");
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState("");
  const [mediaType, setMediaType] = useState("");

  // Configurações Anti-ban
  const [minDelay, setMinDelay] = useState(20);
  const [maxDelay, setMaxDelay] = useState(45);
  const [batchSize, setBatchSize] = useState(15);
  const [batchPauseMin, setBatchPauseMin] = useState(5);

  // Estado Global de Envio da Campanha
  const {
    isRunning,
    isPaused,
    progress,
    stats,
    currentContact,
    countdown,
    logs: globalLogs,
    startBroadcast: startGlobalBroadcast,
    stopBroadcast,
    togglePause,
    openMonitor,
    addLog,
  } = useBroadcastContext();

  // Gravação de áudio ao vivo
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTimer, setRecordingTimer] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);



    const startRecordingAudio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const options = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? { mimeType: "audio/webm;codecs=opus" }
        : undefined;

      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        const file = new File([blob], "audio_gravado.webm", { type: blob.type });
        setAudioFile(file);
        setAudioPreviewUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
        addLog("🎙️ Áudio gravado com sucesso! Pronto para disparo.", "#34d399");
      };

      recorder.start();
      setIsRecording(true);
      setRecordingTimer(0);
      timerIntervalRef.current = setInterval(() => {
        setRecordingTimer((t) => t + 1);
      }, 1000);
      addLog("🔴 Gravando áudio pelo microfone...", "#fbbf24");
    } catch (err) {
      toast.error("Não foi possível acessar o microfone.");
    }
  };

  const stopRecordingAudio = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerIntervalRef.current);
    }
  };

  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAudioFile(file);
      setAudioPreviewUrl(URL.createObjectURL(file));
      addLog(`🎵 Áudio selecionado: ${file.name}. Será enviado como PTT nativo (onda verde).`, "#34d399");
    }
  };

  const removeAudio = () => {
    setAudioFile(null);
    setAudioPreviewUrl("");
  };

  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMediaFile(file);
      setMediaPreviewUrl(URL.createObjectURL(file));
      if (file.type.startsWith("image/")) {
        setMediaType("image");
      } else if (file.type.startsWith("video/")) {
        setMediaType("video");
      } else {
        setMediaType("document");
      }
      addLog(`📎 Mídia anexada: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`, "#38bdf8");
    }
  };

  const removeMedia = () => {
    setMediaFile(null);
    setMediaPreviewUrl("");
    setMediaType("");
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      const lines = content.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
      const parsed = [];

      lines.forEach((line, index) => {
        const cols = line.split(/[,;\t]/).map((c) => c.replace(/["']/g, "").trim());
        if (index === 0 && (cols[0].toLowerCase().includes("tel") || cols[0].toLowerCase().includes("contato") || cols[0].toLowerCase().includes("phone"))) {
          return;
        }

        let num = "";
        let name = "";
        cols.forEach((c) => {
          const clean = c.replace(/\D/g, "");
          if (clean.length >= 8 && !num) {
            num = clean;
          } else if (c.length > 1 && !name && isNaN(c)) {
            name = c;
          }
        });

        if (num) {
          parsed.push({ number: num, name: name });
        }
      });

      setCsvContacts(parsed);
      addLog(`📁 Planilha importada com ${parsed.length} contatos válidos.`, "#38bdf8");
    };
    reader.readAsText(file);
  };

  const getRecipientList = () => {
    let rawList = [];
    if (mode === "csv") {
      rawList = csvContacts;
    } else {
      const lines = manualNumbers.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
      lines.forEach((line) => {
        const parts = line.split(/[,;\t]/).map((p) => p.trim());
        const rawNum = parts[0] ? parts[0].replace(/\D/g, "") : "";
        const name = parts[1] || "";
        if (rawNum.length >= 8) {
          rawList.push({ number: rawNum, name: name });
        }
      });
    }

    // Deduplicação dentro da mesma campanha (evita disparar 2x para o mesmo número no mesmo disparo)
    const seen = new Set();
    const uniqueList = [];
    for (const item of rawList) {
      const clean = String(item.number).replace(/\D/g, "");
      const norm = (clean.length === 10 || clean.length === 11) ? `55${clean}` : clean;
      if (!seen.has(norm)) {
        seen.add(norm);
        uniqueList.push(item);
      }
    }
    return uniqueList;
  };

  const handleStartBroadcast = () => {
    const recipients = getRecipientList();
    if (recipients.length === 0) {
      toast.error("Por favor, informe ao menos um número de telefone.");
      return;
    }

    if (!messageText.trim() && !audioFile && !mediaFile) {
      toast.error("Por favor, digite uma mensagem, selecione um áudio ou anexe uma imagem/vídeo.");
      return;
    }

    startGlobalBroadcast({
      recipients,
      messageText,
      mediaFile,
      audioFile,
      minDelay,
      maxDelay,
      batchSize,
      pauseMinutes: batchPauseMin,
    });
  };

  const totalContacts = getRecipientList().length;

  return (
    <MainContainer>
      <MainHeader>
        <Title>Disparador Inteligente & Áudios Humanizados (Anti-Ban)</Title>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          Envie mensagens e áudios que parecem gravados na hora (PTT verde com sinal de "gravando áudio...") e configure cadência inteligente para evitar qualquer risco de banimento.
        </Typography>

        <Grid container spacing={3}>
          {/* LADO ESQUERDO: DESTINATÁRIOS E CONFIGS */}
          <Grid item xs={12} md={5}>
            <Typography className={classes.sectionTitle}>1. Destinatários</Typography>
            <div className={classes.cardBox}>
              <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
                <FormControlLabel value="manual" control={<Radio color="primary" />} label="Colar Telefones" />
                <FormControlLabel value="csv" control={<Radio color="primary" />} label="Subir Planilha (.CSV)" />
              </RadioGroup>

              {mode === "manual" && (
                <TextField
                  label="Telefones (um por linha com DDD)"
                  multiline
                  rows={6}
                  fullWidth
                  variant="outlined"
                  placeholder="11999998888, João&#10;21988887777, Maria"
                  value={manualNumbers}
                  onChange={(e) => setManualNumbers(e.target.value)}
                  helperText="Ex: 11999998888 ou 11999998888, Nome"
                  style={{ marginTop: 8 }}
                />
              )}

              {mode === "csv" && (
                <Box mt={1}>
                  <input type="file" accept=".csv,.txt" id="csv-page-input" style={{ display: "none" }} onChange={handleCsvUpload} />
                  <label htmlFor="csv-page-input">
                    <Button variant="outlined" color="primary" component="span">
                      Carregar Arquivo .CSV
                    </Button>
                  </label>
                  {csvFileName && (
                    <Typography variant="caption" style={{ display: "block", marginTop: 8 }}>
                      Arquivo: <b>{csvFileName}</b> ({csvContacts.length} contatos)
                    </Typography>
                  )}
                </Box>
              )}

              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 12 }}>
                Total de contatos válidos: <b>{totalContacts}</b>
              </Typography>
            </div>

            <Typography className={classes.sectionTitle}>2. Proteção Anti-Bloqueio (Anti-Ban)</Typography>
            <div className={classes.cardBox}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Delay Mín (seg)"
                    type="number"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={minDelay}
                    onChange={(e) => setMinDelay(Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Delay Máx (seg)"
                    type="number"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={maxDelay}
                    onChange={(e) => setMaxDelay(Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Pausar a cada"
                    type="number"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={batchSize}
                    onChange={(e) => setBatchSize(Number(e.target.value))}
                    helperText="Nº de mensagens"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Tempo de Pausa"
                    type="number"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={batchPauseMin}
                    onChange={(e) => setBatchPauseMin(Number(e.target.value))}
                    helperText="Minutos"
                  />
                </Grid>
              </Grid>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 12 }}>
                O sistema varia o tempo de envio aleatoriamente entre cada mensagem e simula "digitando" / "gravando áudio" no WhatsApp do cliente.
              </Typography>
            </div>
          </Grid>

          {/* LADO DIREITO: CONTEÚDO E DISPARO */}
          <Grid item xs={12} md={7}>
            <Typography className={classes.sectionTitle}>3. Conteúdo</Typography>
            <div className={classes.cardBox}>
              <Box mb={2}>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  Áudio Gravado na Hora (PTT com microfone verde):
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  O cliente receberá como se você tivesse gravado ao vivo (sem etiqueta de encaminhado).
                </Typography>
                <Box mt={1} display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
                  {!isRecording ? (
                    <Button
                      variant="contained"
                      size="small"
                      style={{ backgroundColor: "#ef4444", color: "#fff", fontWeight: "bold" }}
                      onClick={startRecordingAudio}
                    >
                      <MicIcon fontSize="small" style={{ marginRight: 4 }} /> Gravar Áudio Agora
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      style={{ backgroundColor: "#dc2626", color: "#fff", fontWeight: "bold" }}
                      onClick={stopRecordingAudio}
                    >
                      <StopIcon fontSize="small" style={{ marginRight: 4 }} /> Parar Gravação ({recordingTimer}s)
                    </Button>
                  )}

                  <input type="file" accept="audio/*,.mp3,.ogg,.m4a" id="audio-page-input" style={{ display: "none" }} onChange={handleAudioChange} />
                  <label htmlFor="audio-page-input">
                    <Button variant="outlined" size="small" component="span" color="primary">
                      Ou Escolher Arquivo (.mp3 / .ogg)
                    </Button>
                  </label>

                  {audioFile && (
                    <Button size="small" color="secondary" onClick={removeAudio} style={{ marginLeft: 8 }}>
                      Remover Áudio
                    </Button>
                  )}
                </Box>
                {audioPreviewUrl && (
                  <audio controls src={audioPreviewUrl} className={classes.audioPlayer} />
                )}
              </Box>

              <Divider style={{ margin: "16px 0" }} />

              <Box mb={2}>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  Anexar Imagem, Vídeo ou Arquivo:
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Fotos (.jpg, .png), vídeos curtos (.mp4) ou documentos (.pdf).
                </Typography>
                <Box mt={1} display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
                  <input
                    type="file"
                    accept="image/*,video/*,.pdf"
                    id="media-page-input"
                    style={{ display: "none" }}
                    onChange={handleMediaChange}
                  />
                  <label htmlFor="media-page-input">
                    <Button variant="outlined" size="small" component="span" color="primary">
                      <AttachFileIcon fontSize="small" style={{ marginRight: 4 }} />
                      Escolher Imagem / Vídeo / PDF
                    </Button>
                  </label>
                  {mediaFile && (
                    <Button size="small" color="secondary" onClick={removeMedia} style={{ marginLeft: 8 }}>
                      Remover Arquivo
                    </Button>
                  )}
                </Box>
                {mediaPreviewUrl && (
                  <Box mt={1}>
                    {mediaType === "image" && (
                      <img
                        src={mediaPreviewUrl}
                        alt="Preview"
                        style={{ maxHeight: 140, maxWidth: "100%", borderRadius: 8, border: "1px solid #ccc" }}
                      />
                    )}
                    {mediaType === "video" && (
                      <video
                        src={mediaPreviewUrl}
                        controls
                        style={{ maxHeight: 160, maxWidth: "100%", borderRadius: 8 }}
                      />
                    )}
                    {mediaType === "document" && (
                      <Typography variant="caption" style={{ padding: "4px 8px", backgroundColor: "#e2e8f0", borderRadius: 4, display: "inline-block" }}>
                        📄 {mediaFile.name} ({(mediaFile.size / 1024 / 1024).toFixed(2)} MB)
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Divider style={{ margin: "16px 0" }} />

              <Box mt={2}>
                <TextField
                  label="Mensagem de Texto / Legenda da Foto ou Vídeo"
                  multiline
                  rows={4}
                  fullWidth
                  variant="outlined"
                  placeholder="Oi {nome}, tudo bem? Aqui é da coordenação..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  helperText="A tag {nome} é substituída pelo primeiro nome de cada contato. Se anexar foto/vídeo, o texto vira legenda."
                />
              </Box>
            </div>

            <Typography className={classes.sectionTitle}>4. Controle de Envio</Typography>
            <div className={classes.cardBox}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap">
                <div>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {isRunning
                      ? isPaused
                        ? "⏸️ Disparo Pausado"
                        : countdown > 0
                        ? `⏳ Aguardando ${countdown}s (simulando humano)...`
                        : `🚀 Enviando (${stats.current}/${stats.total})...`
                      : stats.total > 0
                      ? `🏁 Disparo finalizado: ${stats.sent} enviados, ${stats.failed} falhas.`
                      : "Pronto para iniciar"}
                  </Typography>
                  {isRunning && currentContact && (
                    <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                      Alvo atual: {currentContact.name ? `${currentContact.name} - ` : ""}{currentContact.number}
                    </Typography>
                  )}
                </div>

                {isRunning ? (
                  <Box display="flex" alignItems="center" mt={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={togglePause}
                      style={{
                        borderColor: isPaused ? "#10b981" : "#f59e0b",
                        color: isPaused ? "#10b981" : "#f59e0b",
                        marginRight: 8,
                      }}
                    >
                      {isPaused ? "Retomar" : "Pausar"}
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={stopBroadcast}
                      style={{ marginRight: 8 }}
                    >
                      Interromper
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={openMonitor}
                      style={{ borderColor: "#38bdf8", color: "#38bdf8" }}
                    >
                      Flutuante
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    style={{ backgroundColor: "#10b981", color: "#fff", fontWeight: "bold" }}
                    onClick={handleStartBroadcast}
                  >
                    🚀 Iniciar Disparo Seguro ({totalContacts})
                  </Button>
                )}
              </Box>

              {isRunning && (
                <Box mt={1} mb={2}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" color="textSecondary">
                      Progresso: {stats.sent + stats.failed}/{stats.total} contatos
                    </Typography>
                    <Typography variant="caption" style={{ fontWeight: 700, color: "#10b981" }}>
                      {progress}%
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={progress} />
                </Box>
              )}

              {globalLogs.length > 0 && (
                <div className={classes.logContainer}>
                  {globalLogs.map((log, index) => (
                    <div key={index} style={{ color: log.color }}>
                      [{log.timestamp}] {log.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Grid>
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default Broadcast;
